import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  IconButton,
} from '@mui/material';
import {
  InsertDriveFile as ExcelIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';

const TableRenderer = ({
  columns = [], // Array of column objects with { name } or array of strings for headers
  data = [], // Array of arrays (rows) or array of objects
  totalRecords = null, // Total count (if different from data.length)
  uniqueKey = 'table',
  // Pagination
  page = 0,
  rowsPerPage = 20,
  onPageChange = null,
  onRowsPerPageChange = null,
  rowsPerPageOptions = [20, 50, 100],
  // Download handlers - can be functions that take (fileName) or (result, fileName) or (headers, dataRows, fileName)
  onDownloadExcel = null,
  onDownloadPDF = null,
  excelFileName = null,
  pdfFileName = null,
  downloadData = null, // Additional data needed for download (e.g., result object or {headers, dataRows})
  // Styling
  maxHeight = 600,
  stickyHeader = false,
  size = 'small',
  headerCellSx = {},
  bodyCellSx = {},
  tableContainerSx = {},
  paperSx = {},
  // Other options
  showHeader = true,
  showPagination = true,
  showDownloadButtons = true,
  emptyCellValue = '-',
}) => {
  // Determine if columns is array of objects or strings
  const headers = columns.length > 0 && typeof columns[0] === 'object' 
    ? columns.map(col => col.name) 
    : columns;

  // Determine if data is array of arrays or array of objects
  const rows = data.length > 0 && Array.isArray(data[0])
    ? data
    : data.map(row => headers.map(header => row[header] || ''));

  const displayTotalRecords = totalRecords !== null ? totalRecords : rows.length;
  const paginatedRows = showPagination && onPageChange
    ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : rows;

  const defaultTableContainerSx = {
    maxHeight: maxHeight,
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f1f1',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#888',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#555',
    },
    ...tableContainerSx,
  };

  const defaultHeaderCellSx = {
    backgroundColor: '#EEF2F4',
    color: '#000',
    fontWeight: 600,
    fontSize: '13px',
    ...headerCellSx,
  };

  const defaultBodyCellSx = {
    fontSize: '13px',
    ...bodyCellSx,
  };

  return (
    <Paper sx={{ width: '100%', mt: 2, border: '1px solid #e0e0e0', ...paperSx }}>
      {showHeader && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 1.5, 
          borderBottom: '1px solid #e8eaed',
          backgroundColor: '#f8f9fa'
        }}>
          <Typography variant="body2" sx={{ color: '#005587', fontWeight: 600, fontSize: '13px' }}>
            Total Records: {displayTotalRecords}
          </Typography>
          {showDownloadButtons && (onDownloadExcel || onDownloadPDF) && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {onDownloadExcel && (
                <IconButton
                  size="small"
                  onClick={() => {
                    const fileName = excelFileName || `SiMS_Data_Exploration_${uniqueKey}`;
                    if (downloadData) {
                      // Check if downloadData has headers and dataRows (for markdown tables)
                      if (downloadData.headers && downloadData.dataRows) {
                        onDownloadExcel(downloadData.headers, downloadData.dataRows, fileName);
                      } else {
                        // Otherwise assume it's a result object
                        onDownloadExcel(downloadData, fileName);
                      }
                    } else {
                      onDownloadExcel(fileName);
                    }
                  }}
                  sx={{
                    backgroundColor: '#00A896',
                    color: '#fff',
                    padding: '6px',
                    '&:hover': {
                      backgroundColor: '#008577',
                    },
                  }}
                  title="Download as Excel"
                >
                  <ExcelIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
              {onDownloadPDF && (
                <IconButton
                  size="small"
                  onClick={() => {
                    const fileName = pdfFileName || `SiMS_Data_Exploration_${uniqueKey}`;
                    if (downloadData) {
                      // Check if downloadData has headers and dataRows (for markdown tables)
                      if (downloadData.headers && downloadData.dataRows) {
                        onDownloadPDF(downloadData.headers, downloadData.dataRows, fileName);
                      } else {
                        // Otherwise assume it's a result object
                        onDownloadPDF(downloadData, fileName);
                      }
                    } else {
                      onDownloadPDF(fileName);
                    }
                  }}
                  sx={{
                    backgroundColor: '#d32f2f',
                    color: '#fff',
                    padding: '6px',
                    '&:hover': {
                      backgroundColor: '#b71c1c',
                    },
                  }}
                  title="Download as PDF"
                >
                  <PdfIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      )}
      <TableContainer sx={defaultTableContainerSx}>
        <Table stickyHeader={stickyHeader} size={size}>
          <TableHead>
            <TableRow>
              {headers.map((header, idx) => (
                <TableCell
                  key={idx}
                  sx={defaultHeaderCellSx}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row, rowIdx) => (
              <TableRow key={rowIdx} hover>
                {row.map((cell, cellIdx) => {
                  // Format numeric values with locale string
                  let displayValue = cell || emptyCellValue;
                  if (typeof displayValue === 'number') {
                    displayValue = displayValue.toLocaleString();
                  }
                  return (
                    <TableCell key={cellIdx} sx={defaultBodyCellSx}>
                      {displayValue}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {showPagination && onPageChange && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          sx={{
            borderTop: '1px solid #e8eaed',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            '.MuiTablePagination-toolbar': {
              minHeight: '52px',
            },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              color: '#000',
              fontWeight: 400,
              fontSize: '13px',
            },
            '.MuiTablePagination-select': {
              color: '#000',
              fontWeight: 400,
            },
          }}
        />
      )}
    </Paper>
  );
};

export default TableRenderer;

